import { Injectable } from '@angular/core';
import type { EntityCardHistoryQueryInterface } from '@zaoblako/shared/common/dtos/history/entity-card-history-query.dto';
import type { DealHelpdeskTaskFormInterface } from '@zaoblako/shared/modules/business/dto/deal-helpdesk-task/deal-helpdesk-task-form.dto';
import type { HelpdeskTaskPageInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-task-page.dto';
import type { DealHelpdeskTaskQueryInterface } from '@zaoblako/shared/modules/business/dto/deal-helpdesk-task/deal-helpdesk-task-query.dto';
import type { DealProjectFormInterface } from '@zaoblako/shared/modules/business/dto/deal-project/deal-project-form.dto';
import type { ProjectPageInterface } from '@zaoblako/shared/modules/project/dto/project/project-page.dto';
import type { DealProjectQueryInterface } from '@zaoblako/shared/modules/business/dto/deal-project/deal-project-query.dto';
import type { DealDocumentsQueryInterface } from '@zaoblako/shared/modules/business/dto/deal-documents/deal-documents-query.dto';
import type { DealDocumentsFindInterface } from '@zaoblako/shared/modules/business/dto/deal-documents/deal-documents-find.dto';
import type { DealFileFindInterface } from '@zaoblako/shared/modules/business/dto/deal/deal-file-find.dto';
import type { DealSearchQueryInterface } from '@zaoblako/shared/modules/business/dto/deal/deal-search-query.dto';
import type { FactMinInterface } from '@zaoblako/shared/common/dtos/fact-min.dto';
import type { DealFlatListQueryInterface } from '@zaoblako/shared/modules/business/dto/deal/deal.flat.list.query.dto';
import type { DealSynchronizeHistoryInterface } from '@zaoblako/shared/modules/business/dto/deal/deal-synchronize-history-find.dto';
import type { DealPageInterface } from '@zaoblako/shared/modules/business/dto/deal/deal-page.dto';
import type { DealsTreePageInterface } from '@zaoblako/shared/modules/business/dto/deals-tree/deals-tree-page.dto';
import type { DealsTreeSearchQueryInterface } from '@zaoblako/shared/modules/business/dto/deals-tree/deals.tree.search.query.dto';
import type { ReminderFindInterface } from '@zaoblako/shared/modules/notification/dtos/reminder/reminder-find.dto';
import type { ReminderFormInterface } from '@zaoblako/shared/modules/notification/dtos/reminder/reminder.form.dto';
import type { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import type { DealHistoryPageInterface } from '@zaoblako/shared/modules/business/dto/deal/deal.history.page.dto';
import { BaseService } from '@zaoblako/web/core/services/base.service';
import { Observable } from 'rxjs';
import { DealPlanFactAnalyticsFindInterface } from '@zaoblako/shared/modules/business/dto/deal-widgets/deal-plan-fact-analytics/deal-plan-fact-analytics-find.dto';
import type { RentalRateAnalysisItemInterface } from '@zaoblako/shared/modules/business/dto/rental-rate-analysis/rental-rate-analysis-item.dto';
import type { RentalRateAnalysisQueryInterface } from '@zaoblako/shared/modules/business/dto/rental-rate-analysis/rental-rate-analysis-query.dto';
import type { ReportDescriptorInterface } from '@zaoblako/shared/common/dtos/report/report-descriptor.dto';
import type { CardDescriptorInterface } from '@zaoblako/shared/common/dtos/card/card-descriptor.dto';
import type { DealStatusSwitchInterface } from '@zaoblako/shared/modules/business/dto/deal/deal-status-switch.dto';
import type { DealStatusFindInterface } from '@zaoblako/shared/modules/business/dto/deal-status/deal-status-find.dto';

@Injectable({
  providedIn: 'root'
})
export class DealService extends BaseService {
  protected baseUrl = '/finance/business/deal';

  public search(queryParams?: DealSearchQueryInterface): Observable<DealPageInterface> {
    return this.http.get<DealPageInterface>(`${this.baseUrl}/search`, {
      params: queryParams ? this.objectToHttpParams(queryParams) : {}
    });
  }

  public hierarchy(queryParams: DealsTreeSearchQueryInterface): Observable<DealsTreePageInterface> {
    return this.http.get<DealsTreePageInterface>(`${this.baseUrl}/tree`, {
      params: this.objectToHttpParams(queryParams)
    });
  }

  public flatList(queryParams: DealFlatListQueryInterface): Observable<DealPageInterface> {
    return this.http.get<DealPageInterface>(`${this.baseUrl}/flat`, { params: this.objectToHttpParams(queryParams) });
  }

  public getRegistryDescriptor(): Observable<ReportDescriptorInterface> {
    return this.http.get<ReportDescriptorInterface>(`${this.baseUrl}/registry-descriptor`);
  }

  public getCardDescriptor(): Observable<CardDescriptorInterface> {
    return this.http.get<CardDescriptorInterface>(`${this.baseUrl}/card-descriptor`);
  }

  public synchronizeAll(): Observable<DealSynchronizeHistoryInterface[]> {
    return this.http.get<DealSynchronizeHistoryInterface[]>(`${this.baseUrl}/synchronize`);
  }

  public getDocuments(
    dealId: number,
    queryParams: DealDocumentsQueryInterface
  ): Observable<DealDocumentsFindInterface> {
    return this.http.get<DealDocumentsFindInterface>(`${this.baseUrl}/${dealId}/document`, {
      params: this.objectToHttpParams(queryParams)
    });
  }

  public getHelpdeskTask(
    dealId: number,
    queryParams: DealHelpdeskTaskQueryInterface
  ): Observable<HelpdeskTaskPageInterface> {
    return this.http.get<HelpdeskTaskPageInterface>(`${this.baseUrl}/${dealId}/helpdesk-task`, {
      params: this.objectToHttpParams(queryParams)
    });
  }

  public addHelpdeskTasks(dealId: number, body: DealHelpdeskTaskFormInterface): Observable<null> {
    return this.http.post<null>(`${this.baseUrl}/${dealId}/helpdesk-task`, body);
  }

  public deleteHelpdeskTasks(dealId: number, body: DealHelpdeskTaskFormInterface): Observable<null> {
    return this.http.delete<null>(`${this.baseUrl}/${dealId}/helpdesk-task`, { body });
  }

  public getProjects(dealId: number, queryParams: DealProjectQueryInterface): Observable<ProjectPageInterface> {
    return this.http.get<ProjectPageInterface>(`${this.baseUrl}/${dealId}/project`, { params: queryParams as any });
  }

  public addProjects(dealId: number, body: DealProjectFormInterface): Observable<null> {
    return this.http.post<null>(`${this.baseUrl}/${dealId}/project`, body);
  }

  public deleteProjects(dealId: number, body: DealProjectFormInterface): Observable<null> {
    return this.http.delete<null>(`${this.baseUrl}/${dealId}/project`, { body });
  }

  public getMembers(dealId: number): Observable<UserFindInterface[]> {
    return this.http.get<UserFindInterface[]>(`${this.baseUrl}/${dealId}/participants`);
  }

  public getFiles(dealId: number): Observable<DealFileFindInterface[]> {
    return this.http.get<DealFileFindInterface[]>(`${this.baseUrl}/${dealId}/file`);
  }

  public getHistory(dealId: number, params: EntityCardHistoryQueryInterface): Observable<DealHistoryPageInterface> {
    return this.http.get<DealHistoryPageInterface>(`${this.baseUrl}/${dealId}/history`, {
      params: this.objectToHttpParams(params)
    });
  }

  public getFactTime(id: number): Observable<FactMinInterface> {
    return this.http.get<FactMinInterface>(`${this.baseUrl}/${id}/fact-time`);
  }

  public createReminder(dealId: number, params: ReminderFormInterface): Observable<ReminderFindInterface> {
    return this.http.post<ReminderFindInterface>(`${this.baseUrl}/${dealId}/reminder`, params);
  }

  public removeReminder(dealId: number): Observable<null> {
    return this.http.delete<null>(`${this.baseUrl}/${dealId}/reminder`);
  }

  public createDocumentFileTemplate(dealId: number, templateId: number): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${dealId}/create-document-file-template/${templateId}`, null);
  }

  public planFactAnalytics(id: number): Observable<DealPlanFactAnalyticsFindInterface> {
    return this.http.get<DealPlanFactAnalyticsFindInterface>(`${this.baseUrl}/${id}/plan-fact-analytics`);
  }

  public getRentalRateAnalysis(dealId: number, query: RentalRateAnalysisQueryInterface): Observable<RentalRateAnalysisItemInterface[]> {
    return this.http.get<RentalRateAnalysisItemInterface[]>(`${this.baseUrl}/${dealId}/rental-rate-analysis`, {
      params: this.objectToHttpParams(query)
    });
  }

  public switchStatus(id: number, params: DealStatusSwitchInterface): Observable<DealStatusFindInterface> {
    return this.http.post<DealStatusFindInterface>(`${this.baseUrl}/${id}/switch-status`, params);
  }
}
